import { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "./api";

/**
 * publicFileUrl(fileId)
 * Direct un-signed URL for objects known to be `visibility=public` (partner
 * logos, admin-featured avatars). Backend still gates by visibility, so this
 * only works for files that are actually public — private files will 401.
 */
export function publicFileUrl(fileId) {
  if (!fileId) return "";
  if (/^https?:\/\//i.test(fileId)) return fileId;
  return `${API_BASE}/files/${fileId}`;
}

/**
 * useSignedFile(fileId, opts?)
 * -------------------------------
 * Given a file_id (UUID stored in DB fields such as cv_url, avatar_url,
 * content_url, attachment_url, logo_url), returns { url, loading, error } for
 * a fresh short-lived signed URL. The URL is auto-refreshed shortly before it
 * expires so that <img>/<video>/<iframe> tags keep working across a session.
 *
 * The signing secret lives only on the backend; the frontend NEVER receives it.
 * storage_path is never exposed to the client either — everything is keyed by
 * `file_id`.
 *
 * Backward-compat: if fileId is falsy, url = "". If fileId is a full http(s)
 * URL (e.g. Unsplash/external), url = fileId itself and no fetch is issued.
 */
export function useSignedFile(fileId, opts = {}) {
  const enabled = opts.enabled !== false;
  const [state, setState] = useState({ url: "", loading: !!fileId, error: null });
  const timerRef = useRef(null);

  useEffect(() => {
    if (!fileId || !enabled) {
      setState({ url: fileId || "", loading: false, error: null });
      return;
    }
    // External URLs — pass through
    if (/^https?:\/\//i.test(fileId)) {
      setState({ url: fileId, loading: false, error: null });
      return;
    }
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.post(`/uploads/${fileId}/access-url`);
        if (!alive) return;
        setState({ url: data.url, loading: false, error: null });
        // Schedule refresh 15s before exp
        if (timerRef.current) clearTimeout(timerRef.current);
        const nowSec = Math.floor(Date.now() / 1000);
        const refreshIn = Math.max(10, (data.expires_at || nowSec + 60) - nowSec - 15) * 1000;
        timerRef.current = setTimeout(load, refreshIn);
      } catch (err) {
        if (!alive) return;
        setState({ url: "", loading: false, error: err });
      }
    };
    load();
    return () => {
      alive = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [fileId, enabled]);

  return state;
}

/** Preview <img> whose src is a signed file URL. */
export function SignedImage({ fileId, alt = "", className = "", fallback = null, ...rest }) {
  const { url, loading } = useSignedFile(fileId);
  if (!fileId) return fallback;
  if (loading || !url) {
    return <div className={`bg-slate-100 animate-pulse ${className}`} aria-hidden />;
  }
  return <img src={url} alt={alt} className={className} {...rest} />;
}

/** <video>/<iframe> wrapper for signed media. */
export function SignedMedia({ fileId, kind = "video", className = "", title = "" }) {
  const { url, loading, error } = useSignedFile(fileId);
  if (!fileId) return null;
  if (loading) return <div className={`bg-slate-100 animate-pulse ${className}`} aria-hidden />;
  if (error || !url) return <div className={`text-sm text-red-600 ${className}`}>تعذّر تحميل الملف</div>;
  if (kind === "video") return <video src={url} controls className={className} />;
  if (kind === "iframe") return <iframe src={url} title={title} className={className} />;
  return <a href={url} target="_blank" rel="noreferrer" className={className}>{title || "فتح الملف"}</a>;
}

/**
 * Anchor tag whose href is fetched on click (so we don't waste a signed URL
 * per render). Useful for "download CV" links.
 */
export function SignedLink({ fileId, className = "", children, testid, filename }) {
  const [busy, setBusy] = useState(false);
  const open = async (e) => {
    e.preventDefault();
    if (!fileId || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/uploads/${fileId}/access-url`);
      if (filename) {
        const blob = await (await fetch(data.url)).blob();
        const href = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = href; a.download = filename; a.click();
        setTimeout(() => URL.revokeObjectURL(href), 30000);
      } else {
        window.open(data.url, "_blank", "noopener,noreferrer");
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <a href="#" onClick={open} className={className} data-testid={testid}>
      {children}
    </a>
  );
}
